import { Injectable } from '@angular/core'
import {
    fetchAuthSession,
    fetchUserAttributes,
    getCurrentUser,
    signInWithRedirect,
    signOut as amplifySignOut
} from 'aws-amplify/auth'
import { CognitoAccessToken, CognitoIdToken, CognitoRefreshToken, CognitoUserSession } from 'amazon-cognito-identity-js'
import { from, Observable, of } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { CognitoUserInfo } from './cognito-user-info'

@Injectable({
    providedIn: 'root'
})
export class CognitoAuthService {
    async currentUser(): Promise<CognitoUserInfo | null> {
        try {
            await getCurrentUser()
            const attrs = await fetchUserAttributes()
            const email = attrs.email ?? ''
            const emailVerified = attrs.email_verified === 'true'
            const familyName = attrs.family_name ?? ''
            const givenName = attrs.given_name ?? ''
            const sub = attrs.sub ?? ''
            const identitiesRaw = attrs.identities ?? '[]'
            const parsedIdentities: Array<{ providerType: string }> = JSON.parse(identitiesRaw)

            const session = await fetchAuthSession()
            const payload = (session.tokens?.accessToken?.payload ?? {}) as Record<string, unknown>

            return {
                email,
                emailVerified,
                familyName,
                identityProvider: parsedIdentities[0]?.providerType ?? '',
                givenName,
                sub,
                userSessionPayload: payload
            }
        } catch {
            return null
        }
    }

    signInGoogle(): Promise<void> {
        return signInWithRedirect({ provider: 'Google' })
    }

    signOut(): Promise<void> {
        return amplifySignOut()
    }

    async getCurrentSession(): Promise<CognitoUserSession> {
        return this.sessionFromAmplify(false)
    }

    getAccessToken(): Observable<CognitoAccessToken> {
        return from(this.getCurrentSession()).pipe(
            switchMap((currentSession: CognitoUserSession) => {
                const accessTokenExpiration = currentSession.getAccessToken().getExpiration()
                const currentTimeSeconds = Math.round(+new Date() / 1000)

                if (accessTokenExpiration < currentTimeSeconds) {
                    return from(this.sessionFromAmplify(true)).pipe(map((s) => s.getAccessToken()))
                }

                return of(currentSession.getAccessToken())
            })
        )
    }

    refreshSession(): Observable<CognitoUserSession> {
        return from(
            (async () => {
                try {
                    return await this.sessionFromAmplify(true)
                } catch (error) {
                    await amplifySignOut()
                    throw error
                }
            })()
        )
    }

    private async sessionFromAmplify(forceRefresh: boolean): Promise<CognitoUserSession> {
        const session = await fetchAuthSession({ forceRefresh })
        const tokens = session.tokens as
            | {
                  accessToken?: { toString(): string }
                  idToken?: { toString(): string }
                  refreshToken?: { toString(): string }
              }
            | undefined
        const accessJwt = tokens?.accessToken?.toString()
        const idJwt = tokens?.idToken?.toString()
        const refreshJwt = tokens?.refreshToken?.toString()

        if (!accessJwt) {
            throw new Error('Not authenticated')
        }

        return new CognitoUserSession({
            IdToken: new CognitoIdToken({ IdToken: idJwt ?? accessJwt }),
            AccessToken: new CognitoAccessToken({ AccessToken: accessJwt }),
            ...(refreshJwt ? { RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshJwt }) } : {})
        })
    }
}
